import {
  ConfirmModal,
  Dropdown,
  DropdownContent,
  DropdownEmpty,
  DropdownItem,
  DropdownLoading,
  DropdownTriggerButton,
  FormSection,
} from '@/components';
import { useLanguages } from '@/features/translations/hooks/useTranslations';
import { useTranslation } from 'react-i18next';
import { DocumentFileObjectRow } from './DocumentFileObjectRow';
import type { FileObjectRowsActions, FileObjectRowsState } from './types';

export interface DocumentFileObjectsSectionActions {
  onAddRowWithLanguage: (languageId: number) => void;
  onLanguageChange: (rowId: string, languageId: number | null) => void;
  onNameChange: (rowId: string, name: string) => void;
  onFileSelect: (rowId: string, file: File | null) => void;
  onValidationError: (rowId: string, error: string) => void;
  onDeleteRow: (rowId: string) => void;
  onConfirmDelete: (rowId: string) => void;
  onCancelDelete: () => void;
  onOpen: (rowId: string, download : boolean) => Promise<void>;
  onUploadFile: (rowId: string, file: File) => void;
}

interface DocumentFileObjectsSectionProps {
  fileObjectsState: FileObjectRowsState;
  validationErrors: Record<string, string>;
  isLocked: boolean;
  actions: DocumentFileObjectsSectionActions;
  // Detail-mode props
  mode?: 'create' | 'detail';
  availabilityFilter?: 'available' | 'all';
  onAvailabilityFilterChange?: (filter: 'available' | 'all') => void;
}

/**
 * @component DocumentFileObjectsSection
 * @description Renders the file object collection for document file forms, including add/remove actions and per-row upload controls.
 *
 * @props
 *   rows (FileObjectRow[], required) - The file object rows to display.
 *   validationErrors (Record<string, string>, required) - Validation errors keyed by rowId.
 *   isLocked (boolean, required) - Disables editing while metadata is read-only.
 *   actions (DocumentFileObjectsSectionActions, required) - Action callbacks for row operations.
 *   mode ('create' | 'detail', optional) - Defaults to 'create'. Detail mode enables availability
 *     toggle, per-row upload/download, and immediate row deletion.
 *   availabilityFilter ('available' | 'all', optional) - Detail mode only. Controls which rows are visible.
 *   onAvailabilityFilterChange ((filter) => void, optional) - Detail mode only. Called when toggle changes.
 *   onDownload ((rowId: string) => void, optional) - Detail mode only. Called on download button click.
 *   onUpload ((rowId: string, file: File) => void, optional) - Detail mode only. Called when file is picked.
 *   onDeleteRow ((rowId: string) => void, optional) - Detail mode only. Called when remove is confirmed.
 *
 * @changelog
 *   2026-03-24 - Initial creation for shared document file create/edit flows.
 *   2026-03-24 - Refactored to pure UI component receiving state and actions as props.
 *   2026-03-25 - Language selection moved to Dropdown picker on "Add language" and inline header edit.
 *   2026-03-25 - Added mode='detail' support: availability toggle, upload/download callbacks.
 */
export function DocumentFileObjectsSection({
  fileObjectsState,
  validationErrors,
  isLocked,
  actions,
  mode = 'create',
  availabilityFilter = 'available',
  onAvailabilityFilterChange
}: DocumentFileObjectsSectionProps) {
  const { t } = useTranslation();
  const {
    data: languages = [],
    isLoading: isLoadingLanguages,
    isError: hasLanguageError,
  } = useLanguages();

  const { rows } = fileObjectsState;

  const isDetailMode = mode === 'detail';
  const activeRows = rows.filter((row) => row.changeState !== 'deleted');

  const usedLanguageIds = new Set(activeRows.map((row) => row.values.languageId).filter(Boolean));
  const availableForAdd = languages.filter(
    (lang) => !usedLanguageIds.has(lang.id)
  );
  const allLanguagesAssigned = !isLoadingLanguages && !hasLanguageError && availableForAdd.length === 0;

  const renderAddDropdownContent = () => {
    if (isLoadingLanguages) {
      return <DropdownLoading rows={3} />;
    }
    if (hasLanguageError) {
      return <DropdownEmpty>{t('documents.createFile.failedToLoad')}</DropdownEmpty>;
    }
    if (availableForAdd.length === 0) {
      return <DropdownEmpty>{t('documents.createFile.noLanguagesAvailable')}</DropdownEmpty>;
    }
    return availableForAdd.map((lang) => (
      <DropdownItem key={lang.id} onSelect={() => actions.onAddRowWithLanguage(lang.id)}>
        {lang.name}
      </DropdownItem>
    ));
  };

  const addLanguageDropdown = (
    <Dropdown>
      <DropdownTriggerButton
        disabled={isLocked || allLanguagesAssigned}
        className="text-primary border-none bg-transparent shadow-none hover:bg-transparent focus:bg-transparent text-sm font-medium px-0 py-0 disabled:opacity-50"
      >
        {t('documents.createFile.addFileObject')}
      </DropdownTriggerButton>
      <DropdownContent>
        {renderAddDropdownContent()}
      </DropdownContent>
    </Dropdown>
  );


  const pendingDeleteRow = fileObjectsState.pendingDeleteObjectId
    ? fileObjectsState.rows.find((r) => r.rowId === fileObjectsState.pendingDeleteObjectId)
    : null;


  return (<>
    <FormSection
      icon="languages"
      title={t('documents.createFile.sections.filesAndLanguages')}
    >
      {isDetailMode && onAvailabilityFilterChange ? (
        <div className="mb-3 flex gap-1 rounded-lg border border-border bg-surface-darker/30 p-1 w-fit">
          <button
            type="button"
            onClick={() => onAvailabilityFilterChange('available')}
            className={[
              'rounded-md px-3 py-1 text-xs font-medium transition-colors',
              availabilityFilter === 'available'
                ? 'bg-surface text-onsurface shadow-sm'
                : 'text-onsurface/60 hover:text-onsurface',
            ].join(' ')}
          >
            {t('documents.detail.availabilityFilter.available')}
          </button>
          <button
            type="button"
            onClick={() => onAvailabilityFilterChange('all')}
            className={[
              'rounded-md px-3 py-1 text-xs font-medium transition-colors',
              availabilityFilter === 'all'
                ? 'bg-surface text-onsurface shadow-sm'
                : 'text-onsurface/60 hover:text-onsurface',
            ].join(' ')}
          >
            {t('documents.detail.availabilityFilter.all')}
          </button>
        </div>
      ) : null}

      {activeRows.length === 0 ? (
        <div className="border-border rounded-xl border border-dashed p-6 flex flex-col items-center gap-3 text-center">
          <p className="text-onsurface/60 text-sm">
            {t('documents.createFile.emptyFilesDescription')}
          </p>
          {!isLocked && addLanguageDropdown}
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {activeRows.map((row, index) => (
              <DocumentFileObjectRow
                key={row.rowId}
                fileObject={row}
                index={index}
                rowCount={activeRows.length}
                isLocked={isLocked}
                validationError={validationErrors[row.rowId]}
                languages={languages}
                availableForAdd={availableForAdd}
                isLoadingLanguages={isLoadingLanguages}
                hasLanguageError={hasLanguageError}
                onLanguageChange={actions.onLanguageChange}
                onNameChange={actions.onNameChange}
                onFileSelect={actions.onFileSelect}
                onValidationError={actions.onValidationError}
                onRemove={(id) => {
                  actions.onDeleteRow(id)
                }}
                mode={mode}
                onOpen={actions.onOpen}
                onUpload={actions.onUploadFile}
              />
            ))}
          </div>
          {!isLocked && !allLanguagesAssigned && (
            <div className="flex justify-end">
              {addLanguageDropdown}
            </div>
          )}
        </>
      )}
    </FormSection>



    {/* File object delete confirmation */}
    <ConfirmModal
      isOpen={Boolean(pendingDeleteRow)}
      title={t('documents.detail.fileObjects.deleteConfirmTitle')}
      message={t('documents.detail.fileObjects.deleteConfirmMessage')}
      confirmLabel={t('documents.detail.deleteFile')}
      cancelLabel={t('common.cancel', { defaultValue: 'Cancel' })}
      variant="danger"
      isLoading={fileObjectsState.isDeletingObject}
      onConfirm={() => {
        if (fileObjectsState.pendingDeleteObjectId) {
          actions.onConfirmDelete(fileObjectsState.pendingDeleteObjectId)
        }
      }}
      onCancel={() => {
        actions.onCancelDelete()
      }}
    />
  </>
  );
}

export function createFileObjectsSectionActions(
  hookActions: FileObjectRowsActions
): DocumentFileObjectsSectionActions {
  return {
    onAddRowWithLanguage: hookActions.addRowWithLanguage,
    onDeleteRow: hookActions.deleteRow,
    onConfirmDelete: hookActions.confirmDeleteRow,
    onCancelDelete: hookActions.cancleDeleteRequest,
    onLanguageChange: hookActions.updateLanguage,
    onNameChange: hookActions.updateName,
    onFileSelect: hookActions.updateFile,
    onValidationError: hookActions.setValidationError,
    onOpen: hookActions.onOpenFile,
    onUploadFile: hookActions.reuploadFileObject,
  };
}
